import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { completeHostedLogin, loginWithPassword, logout, resetPassword, ssoLoginOptions } from '../gql/consts';
import { LocalStoreService } from './localstore.service';

import { catchError, map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { PlayerService } from './player.service';
import {
  authRefusalMessage,
  defaultCallbackUrl,
  hostedLoginRedirect,
  withRedirectUri,
} from '../utils/hostedLoginClient';

export interface SsoLoginOptions {
  ssoAvailable: boolean;
  ssoRequired: boolean;
  providerName: string | null;
  providerKind: string | null;
  startUrl: string | null;
}

/** A refusal worth showing as-is (disabled account, SSO/MFA problem) rather than "incorrect password". */
export class UserFacingAuthError extends Error {
  readonly userFacing = true;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isAuthorized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private apollo: Apollo,
    private localstoreService: LocalStoreService,
    private playerService: PlayerService
  ) { }

  public isAuthenticated(): boolean {
    const token = this.localstoreService.getItem('authToken');
    this.isAuthorized$.next(!!token);

    return !!token;
  }

  private storeToken(token: string) {
    this.localstoreService.setItem('authToken', token);
    this.isAuthorized$.next(true);
  }

  /** Emits `redirecting: true` when the sign-in continues on api-v2's hosted SSO/MFA pages. */
  login(email: string, password: string): Observable<{ redirecting: boolean }> {
    return this.apollo.mutate({
        mutation: loginWithPassword,
        variables: { email, password }
      })
      .pipe(
        map((data: any) => data.data.loginWithPassword),
        tap((authData) => this.storeToken(authData.loginToken)),
        map(() => ({ redirecting: false })),
        catchError((error) => {
          const hosted = hostedLoginRedirect(error, defaultCallbackUrl(window));
          if (hosted && hosted.url) {
            window.location.assign(hosted.url);
            return of({ redirecting: true });
          }
          const message = (hosted && hosted.message) || authRefusalMessage(error);
          return throwError(message ? new UserFacingAuthError(message) : error);
        })
      );
  }

  ssoLoginOptions(email: string): Observable<SsoLoginOptions | null> {
    return this.apollo.query({
        query: ssoLoginOptions,
        variables: { email },
        fetchPolicy: 'no-cache'
      })
      .pipe(
        map((result: any) => (result && result.data && result.data.ssoLoginOptions) || null),
        catchError(() => of(null))
      );
  }

  ssoStartUrl(startUrl: string | null): string | null {
    return withRedirectUri(startUrl, defaultCallbackUrl(window));
  }

  /** Exchanges the one-time #audCode the hosted pages returned for a session. */
  completeHostedLogin(handoffCode: string): Observable<void> {
    return this.apollo.mutate({
        mutation: completeHostedLogin,
        variables: { handoffCode }
      })
      .pipe(
        map((result: any) => {
          const token = result && result.data && result.data.completeHostedLogin && result.data.completeHostedLogin.loginToken;
          if (!token) {
            throw new UserFacingAuthError('This sign-in link has expired or was already used. Start again.');
          }
          this.storeToken(token);
        })
      );
  }

  logout() {
    return this.apollo.mutate({
        mutation: logout
      })
      .pipe(
        tap(() => {
          this.localstoreService.clear();
          this.isAuthorized$.next(false);
          this.playerService.clear();
        })
      );
  }

  resetPassword(email: string) {
    return this.apollo.mutate({
      mutation: resetPassword,
      variables: { email }
    });
  }
}
